import { arrayMoveImmutable } from 'array-move';

import { AppContextType, ActionType } from './types';
import {
  COLLAPSE_SIDER,
  EXPAND_SIDER,
  SET_INITIAL_DATA,
  DELETE_ROWS,
  CUT_ROWS,
  COPY_ROWS,
  ADD_ROW_ABOVE,
  ADD_ROW_BELOW,
  PASTE_AT_BOTTOM,
  PASTE_AFTER_ROW,
  UPDATE_ROW,
  CANCEL_ROW_ADD,
  UPDATE_CELLS,
  CANCEL_CELLS_UPDATE,
  CANCEL_BULK_ACTION,
  UPDATE_MASTER_LIST_ID
} from './rows/actionTypes';
import {
  ADD_TAB,
  UPDATE_TAB,
  DELETE_TAB,
  CANCEL_ADD_TAB,
  ADD_CREATED_TAB,
  SORT_ITEMS,
  RESET_TABS,
  SAVE_SORTED_TABS,
  SHOW_HIDE_ALL_TABS,
  SHOW_HIDE_TAB,
  SET_ACTIVE_TAB,
  SET_PROJECT_DATA
} from './tabs/actionTypes';
import { SET_USER_PROFILE, SET_USER, TOGGLE_USER_SETTINGS } from './user/actionTypes';
import { getUpdatedTabs } from '../utils/getUpdatedTabs';
import { updateLocalTabs } from '../utils/updateLocalTabs';
import { ProjectDataDto, ProjectTabDto } from '../api_client/parameter_api';
import { getUpdatedLocalTabIds } from '../utils/getUpdatedLocalTabIds';
import { isAllTabsHidden } from '../utils/isAllTabsHidden';
import { ProjectData } from '../pages/Project/types';
import { PROJECT_MOCK_DATA } from '../pages/Project/config';
import { getUpdatedProjectData } from '../utils/getUpdatedProjectData';

const reducer = (state: AppContextType, action: ActionType): AppContextType => {
  switch (action.type) {
    case COLLAPSE_SIDER:
      return {
        ...state,
        siderIsCollapsed: true
      };
    case EXPAND_SIDER:
      return {
        ...state,
        siderIsCollapsed: false
      };
    case SET_INITIAL_DATA: {
      const { tabs, activeTab, tableData, projectSlug, activePage } = action.payload;
      const localTabHiddenIds = localStorage.getItem(projectSlug);
      //in local storage will be stored hidden tab ids
      let updatedTabs = [...tabs];
      let updateAllTabsHidden = state.allTabsHidden;
      if (localTabHiddenIds && !!localTabHiddenIds.length) {
        const parsedLocalTabIds = JSON.parse(localTabHiddenIds);
        updatedTabs = tabs.map((tab: ProjectTabDto) => ({
          ...tab,
          hidden: parsedLocalTabIds.includes(tab.id)
        }));
        if (parsedLocalTabIds.length === tabs.length) {
          updateAllTabsHidden = true;
        }
      }
      return {
        ...state,
        tabs: updatedTabs,
        data: tableData,
        clonedTabs: updatedTabs,
        allTabsHidden: updateAllTabsHidden,
        activePage: activePage,
        activeTab: activeTab,
        siderIsCollapsed: true
      };
    }
    case SET_ACTIVE_TAB: {
      const { tab } = action.payload;
      return { ...state, activeTab: tab, isTabChanged: !!state.bulkAction };
    }
    case DELETE_ROWS: {
      const { selectedRowIds } = action.payload;
      const updatedData = state.data.filter(item => !selectedRowIds.includes(item.id));
      return { ...state, data: updatedData };
    }
    case CUT_ROWS:
      const { tabSlug } = action.payload;
      return { ...state, prevTabSlug: tabSlug, bulkAction: CUT_ROWS };
    case COPY_ROWS: {
      return { ...state, bulkAction: COPY_ROWS };
    }
    case ADD_ROW_BELOW:
    case ADD_ROW_ABOVE: {
      const { index } = action.payload;
      const updatedData = [...state.data];
      const incDecVal = action.type === ADD_ROW_BELOW ? 1 : 0;
      updatedData.splice(index + incDecVal, 0, PROJECT_MOCK_DATA);
      return { ...state, data: updatedData, addingRowIndex: index + incDecVal };
    }
    case UPDATE_ROW: {
      const { rowData, index } = action.payload;
      const updatedData = [...state.data];
      if (index === -1) {
        //new row
        updatedData[0] = rowData;
      } else {
        //update current row
        updatedData[index] = rowData;
      }
      return { ...state, data: updatedData, cellsUpdated: false, projectData: null };
    }
    case PASTE_AT_BOTTOM: {
      const { newRows, prevMasterListId, currentMasterListId, insideSameTab } = action.payload;
      let updatedData = [...state.data];
      const insertedRowIds: Record<string, any> = {};
      if (prevMasterListId !== currentMasterListId && typeof prevMasterListId === typeof currentMasterListId) {
        newRows.forEach((row: ProjectDataDto) => {
          insertedRowIds[row.id as number] = true;
        });
      }

      if (state.bulkAction === CUT_ROWS && insideSameTab) {
        //weather or not we have cut&paste in same tab
        const newRowIds: Array<number> = newRows.map((row: ProjectData) => row.id);
        updatedData = state.data.filter(item => !newRowIds.includes(item.id as number));
      }

      return {
        ...state,
        data: getUpdatedProjectData([...updatedData, ...newRows]),
        bulkAction: '',
        isTabChanged: false,
        insertedRows: {
          masterListId: prevMasterListId,
          ids: insertedRowIds
        }
      };
    }

    case PASTE_AFTER_ROW: {
      const { newRows, prevMasterListId, currentMasterListId, rowId, insideSameTab } = action.payload;
      let updatedData = [...state.data];
      const insertedRowIds: Record<string, any> = {};
      if (prevMasterListId !== currentMasterListId && typeof prevMasterListId === typeof currentMasterListId) {
        newRows.forEach((row: ProjectDataDto) => {
          insertedRowIds[row.id as number] = true;
        });
      }

      if (state.bulkAction === CUT_ROWS && insideSameTab) {
        //weather or not we have cut&paste in same tab
        const newRowIds: Array<number> = newRows.map((row: ProjectData) => row.id);
        updatedData = state.data.filter(item => !newRowIds.includes(item.id as number));
      }
      const rowIndex = updatedData.findIndex(row => row.id === rowId);

      updatedData.splice(rowIndex + 1, 0, ...newRows);

      return {
        ...state,
        data: getUpdatedProjectData(updatedData as ProjectData[]),
        bulkAction: '',
        isTabChanged: false,
        insertedRows: {
          masterListId: prevMasterListId,
          ids: insertedRowIds
        }
      };
    }

    case CANCEL_ROW_ADD: {
      const updatedData = [...state.data];
      updatedData.splice(state.addingRowIndex, 1);
      return { ...state, data: updatedData, cellsUpdated: false, projectData: null };
    }

    case SET_PROJECT_DATA: {
      const { projectData } = action.payload;
      return { ...state, data: projectData };
    }

    case UPDATE_CELLS: {
      const { updatedRowData, index } = action.payload;
      const updatedData = [...state.data];
      const rowCurrentData = updatedData[index];
      updatedData[index] = updatedRowData;
      return { ...state, data: updatedData, projectData: rowCurrentData, cellsUpdated: true };
    }

    case CANCEL_CELLS_UPDATE: {
      const { rowIndex } = action.payload;
      const updatedData = [...state.data];
      updatedData[rowIndex] = { ...(state.projectData as ProjectData) };

      return { ...state, data: updatedData, cellsUpdated: false, projectData: null };
    }

    case CANCEL_BULK_ACTION: {
      return { ...state, bulkAction: '', isTabChanged: false };
    }

    case UPDATE_MASTER_LIST_ID: {
      const { id } = action.payload;
      const { ids, masterListId } = state.insertedRows;
      let updatedRowIds = ids;
      if (id === masterListId) {
        updatedRowIds = {};
      }
      return { ...state, insertedRows: { masterListId, ids: updatedRowIds } };
    }

    //Tabs section
    case ADD_TAB: {
      const { tab } = action.payload;
      const updatedTabs = [tab, ...state.tabs];
      return { ...state, tabs: updatedTabs };
    }
    case UPDATE_TAB: {
      const { tab, index } = action.payload;
      const updatedTabs = [...state.tabs];
      updatedTabs[index] = tab;
      return { ...state, tabs: updatedTabs, clonedTabs: updatedTabs };
    }
    case DELETE_TAB: {
      const { projectSlug, slug, index } = action.payload;
      const deletingTab = { ...state.tabs[index], hidden: false };
      const updatedLocalTabIds = getUpdatedLocalTabIds(projectSlug, deletingTab);
      const allTabsHidden = isAllTabsHidden(state.tabs.length, updatedLocalTabIds.length, state.allTabsHidden);

      const updatedTabs = state.tabs.filter(tab => tab.slug !== slug);
      const activeTab = updatedTabs[index] ?? null;

      return {
        ...state,
        tabs: updatedTabs,
        clonedTabs: updatedTabs,
        activeTab: activeTab,
        allTabsHidden: allTabsHidden
      };
    }
    case CANCEL_ADD_TAB: {
      const updatedTabs = [...state.tabs];
      updatedTabs.shift();
      return { ...state, tabs: updatedTabs };
    }
    case ADD_CREATED_TAB: {
      const { tab } = action.payload;
      const updatedTabs = [...state.tabs];
      updatedTabs.splice(0, 1, tab);
      const activeTab = state.activeTab ?? { ...tab };
      return {
        ...state,
        tabs: updatedTabs,
        clonedTabs: updatedTabs,
        activeTab: activeTab
      };
    }
    case SORT_ITEMS: {
      const { oldIndex, newIndex, source } = action.payload;
      let updatedRows = state.data;
      let updatedTabs = state.tabs;
      let tabsSorted = state.tabsSorted;
      if (source === 'ROWS') {
        updatedRows = arrayMoveImmutable(state.data, oldIndex, newIndex);
      } else {
        updatedTabs = arrayMoveImmutable(state.tabs, oldIndex, newIndex);
        tabsSorted = true;
      }

      return { ...state, tabs: updatedTabs, tabsSorted: tabsSorted, data: updatedRows };
    }
    case RESET_TABS: {
      return { ...state, tabs: [...state.clonedTabs], tabsSorted: false };
    }
    case SAVE_SORTED_TABS: {
      return { ...state, clonedTabs: [...state.tabs], tabsSorted: false };
    }
    case SHOW_HIDE_ALL_TABS: {
      const { projectSlug } = action.payload;
      let updateTabs;
      let updateAllTabsHidden;
      if (state.allTabsHidden) {
        updateTabs = getUpdatedTabs(state.tabs, false);
        updateAllTabsHidden = false;
        updateLocalTabs(projectSlug, state.tabs, false);
      } else {
        updateTabs = getUpdatedTabs(state.tabs, true);
        updateAllTabsHidden = true;
        updateLocalTabs(projectSlug, state.tabs, true);
      }

      return { ...state, tabs: updateTabs, allTabsHidden: updateAllTabsHidden, clonedTabs: updateTabs };
    }
    case SHOW_HIDE_TAB: {
      const { projectSlug, tabIndex } = action.payload;
      const updateTabs = [...state.tabs];
      updateTabs[tabIndex].hidden = !updateTabs[tabIndex].hidden;
      const updatedLocalTabIds = getUpdatedLocalTabIds(projectSlug, updateTabs[tabIndex]);
      //update allTabsHidden;
      const allTabsHidden = isAllTabsHidden(state.tabs.length, updatedLocalTabIds.length, state.allTabsHidden);
      return { ...state, tabs: updateTabs, clonedTabs: updateTabs, allTabsHidden: allTabsHidden };
    }

    //user section
    case SET_USER_PROFILE: {
      const { profile } = action.payload;
      return { ...state, userProfile: profile };
    }

    case SET_USER: {
      const { user } = action.payload;
      return { ...state, loggedUser: user };
    }

    case TOGGLE_USER_SETTINGS: {
      const { data } = action.payload;
      return { ...state, userSettings: data };
    }

    default:
      return { ...state };
  }
};

export default reducer;
